import type { BoardUser, LikedItem, NCGraphData, NCNode } from '../../commonTypes';
import { getProjectionName, type Board } from '../../models/board.model';
import type { FlowyPlanBoard, IAgentMemory } from '../../models/agentMemory.model';
import type { IFlowyParticipantState } from '../../models/sharedFlowySession.model';
import { getPositioningOutputSnapshots } from '../../positioningOutputs';
import { getBuildingBlock } from './buildingBlocks';
import { truncateFlowyText } from './textTruncation';
import { compactNodeLabel, isNodeLabel } from './types';

const BOARD_LABEL: Record<FlowyPlanBoard, string> = {
	inspire: 'Inspire',
	ideate: 'Ideate',
	moodboard: 'Synthesise',
};

const EMPTY_BOARD: Record<FlowyPlanBoard, string> = {
	inspire: 'The Inspire board is still empty.',
	ideate: 'The Ideate positioning map is ready, but there are no outputs on it yet.',
	moodboard: 'The Synthesise board is still empty.',
};

const DEFAULT_BLOCK_BY_BOARD: Record<FlowyPlanBoard, string> = {
	inspire: 'explore_problem_space',
	ideate: 'persona_stakeholder_motivations',
	moodboard: 'brainstorming_synthesise',
};

const SOURCE_TO_BLOCK: Record<string, string> = {
	spark: 'explore_problem_space',
	empathise: 'explore_problem_space',
	vision: 'explore_problem_space',
	'onboarding-llm': 'explore_problem_space',
	'onboarding-kg': 'explore_problem_space',
	common: 'explore_problem_space',
	brainstorm: 'explore_problem_space',
	web: 'research_related_work',
	pubmed: 'research_related_work',
	gdelt: 'research_related_work',
	pdf: 'research_related_work',
	link: 'research_related_work',
	search: 'research_related_work',
	haiku: 'generate_haikus',
	'ai-haiku': 'generate_haikus',
	tagline: 'generate_taglines',
	proverb: 'generate_taglines',
	persona: 'persona_stakeholder_motivations',
	motivations: 'persona_stakeholder_motivations',
	'brand-colours': 'define_brand_voice',
	'brand-tone': 'define_brand_voice',
	audience: 'audience_profile',
	strategy: 'brand_strategy',
	post: 'linkedin_post',
	linkedinpost: 'linkedin_post',
	brief: 'product_brief',
	'prod-ideas': 'product_idea',
	'product-idea': 'product_idea',
	'technical-spec': 'product_brief',
	technicalspec: 'product_brief',
	game: 'game',
	synopsis: 'story_synopsis',
	brainstorming: 'brainstorming_synthesise',
	unite: 'brainstorming_synthesise',
	summary: 'brainstorming_synthesise',
	extract: 'brainstorming_synthesise',
};

const BLOCK_ITEM_NOUN: Record<string, string> = {
	explore_problem_space: 'ideas',
	research_related_work: 'sources',
	image_video_generation: 'visual references',
	persona_stakeholder_motivations: 'personas and motivations',
	define_brand_voice: 'brand voice outputs',
	generate_taglines: 'taglines',
	generate_haikus: 'haikus',
};

const LABELS_PER_BLOCK = 4;
const MAX_BLOCKS = 3;

const teamIdeateGraph = (board: Omit<Board, 'addUser'>): NCGraphData | undefined =>
	board.userOne?.ideateTeam?.data || board.ideate?.data;

const graphForScope = (
	board: Omit<Board, 'addUser'>,
	scope: FlowyPlanBoard,
	options?: { preferTeam?: boolean }
): NCGraphData | undefined => {
	if (scope === 'inspire') return board.inspire?.data;
	if (scope === 'ideate') {
		return options?.preferTeam ? teamIdeateGraph(board) : board.ideate?.data;
	}
	return board.moodboard?.data;
};

export const countLikedNodes = (graphData: NCGraphData | undefined): number =>
	(graphData?.nodes || []).filter(
		(node) => !node.hidden && node.likes && Object.values(node.likes).some(Boolean)
	).length;

const sourceKey = (node: NCNode): string | null => {
	for (const topic of node.topics || []) {
		const match = /^source:(.+)$/i.exec(topic.trim());
		if (!match) continue;
		return match[1]
			.trim()
			.toLowerCase()
			.replace(/[\s_]+/g, '-');
	}
	return null;
};

const inferBuildingBlockId = (node: NCNode, scope: FlowyPlanBoard): string => {
	if (node.src || node.isVideo) return 'image_video_generation';
	const source = sourceKey(node);
	if (source) {
		if (source === 'brainstorm' && scope === 'moodboard') return 'brainstorming_synthesise';
		if (source.startsWith('pestel') || source.startsWith('swot')) return 'pestel_swot';
		if (SOURCE_TO_BLOCK[source]) return SOURCE_TO_BLOCK[source];
		if (scope === 'moodboard' && SOURCE_TO_BLOCK[source.split('-')[0]]) {
			return SOURCE_TO_BLOCK[source.split('-')[0]];
		}
	}
	return DEFAULT_BLOCK_BY_BOARD[scope];
};

const displayLabel = (label: string, max = 56): string => {
	const oneLine = label.replace(/\s+/g, ' ').trim();
	if (!oneLine || /^https?:\/\//i.test(oneLine)) return '';
	const colon = oneLine.indexOf(':');
	const title = colon >= 8 && colon <= 80 ? oneLine.slice(0, colon).trim() : oneLine;
	return truncateFlowyText(title, { maxChars: max });
};

const likeScore = (node: NCNode): number =>
	node.likes ? Object.values(node.likes).filter(Boolean).length : 0;

const pickLabels = (nodes: NCNode[], limit = LABELS_PER_BLOCK): string[] => {
	const ranked = [...nodes].sort((a, b) => {
		const likeDiff = likeScore(b) - likeScore(a);
		if (likeDiff) return likeDiff;
		return (b.created || 0) - (a.created || 0);
	});
	const labels: string[] = [];
	for (const node of ranked) {
		const label = displayLabel(compactNodeLabel(node));
		if (!label || labels.includes(label)) continue;
		labels.push(label);
		if (labels.length >= limit) break;
	}
	return labels;
};

const joinLabels = (labels: string[]): string => {
	if (labels.length === 1) return labels[0];
	if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
	return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
};

const blockLabel = (id: string): string => getBuildingBlock(id)?.label || id;

const formatBlockWork = (id: string, nodes: NCNode[]): string => {
	const labels = pickLabels(nodes);
	const extra = Math.max(0, nodes.length - labels.length);
	const noun = BLOCK_ITEM_NOUN[id] || 'outputs';
	if (!labels.length) {
		return extra > 0 ? `**${blockLabel(id)}** added ${extra} ${noun}` : '';
	}
	const listed = joinLabels(labels);
	if (extra > 0) {
		return `**${blockLabel(id)}** added ${listed}, and ${extra} more`;
	}
	return `**${blockLabel(id)}** added ${listed}`;
};

const shortenEssence = (value?: string | null, max = 120): string =>
	truncateFlowyText(value, { maxChars: max });

const planBlockOrder = (memory: IAgentMemory | null | undefined, scope: FlowyPlanBoard): string[] => {
	const fromPlan = (memory?.plan?.steps || [])
		.map((step) => step.buildingBlockId)
		.filter((id): id is string => Boolean(id && getBuildingBlock(id)?.board === scope));
	const completed = memory?.sessionState?.completedBuildingBlockIds || [];
	const active = memory?.sessionState?.activeBuildingBlockId;
	return Array.from(new Set([...fromPlan, ...completed, active].filter(Boolean))) as string[];
};

const describeIdeateExtras = (graphData: NCGraphData | undefined): string[] => {
	const parts: string[] = [];
	const insight = (graphData?.positioningInsight || '').trim();
	if (insight) parts.push(`positioning direction "${shortenEssence(insight, 80)}"`);
	const clusterNames = (graphData?.clusters || [])
		.map((cluster) => (cluster.group || '').trim())
		.filter(Boolean)
		.slice(0, 3);
	if (clusterNames.length) {
		parts.push(`clusters around ${joinLabels(clusterNames)}`);
	}
	return parts;
};

export const boardDisplayName = (board: FlowyPlanBoard): string => BOARD_LABEL[board];

const isEmptyBoardSummary = (summary: string): boolean =>
	/still empty|no positioning outputs yet|no labelled ideas yet|Idea Box is still empty/i.test(
		summary
	);

export const summarizeBoardGraph = ({
	board,
	scope,
	memory = null,
	preferTeam = false,
	omitEmpty = false,
}: {
	board: Omit<Board, 'addUser'> | null | undefined;
	scope: FlowyPlanBoard;
	memory?: IAgentMemory | null;
	/** Prefer Team Ideate / shared graphs when summarising collaborative catch-up. */
	preferTeam?: boolean;
	/** Skip boards with nothing useful yet (catch-up should focus on session juice). */
	omitEmpty?: boolean;
}): string => {
	if (!board) {
		return omitEmpty ? '' : `I do not have a snapshot of the ${BOARD_LABEL[scope]} board yet.`;
	}

	const graphData = graphForScope(board, scope, { preferTeam });
	const scopeLabel =
		preferTeam && scope === 'ideate'
			? 'Team Ideate'
			: preferTeam && scope === 'moodboard'
				? 'Team Synthesise'
				: BOARD_LABEL[scope];
	const nodes = (graphData?.nodes || []).filter((node) => !node.hidden);
	const labelled = nodes.filter(
		(node) => isNodeLabel(node.label) && !/^https?:\/\//i.test(node.label.trim())
	);

	if (!nodes.length && scope !== 'ideate') {
		if (omitEmpty) return '';
		return preferTeam && scope === 'moodboard'
			? 'The Team Synthesise board is still empty.'
			: EMPTY_BOARD[scope];
	}

	const grouped = new Map<string, NCNode[]>();
	for (const node of labelled) {
		const blockId = inferBuildingBlockId(node, scope);
		const list = grouped.get(blockId) || [];
		list.push(node);
		grouped.set(blockId, list);
	}

	const preferredOrder = planBlockOrder(memory, scope);
	const blockIds = [
		...preferredOrder.filter((id) => grouped.has(id)),
		...[...grouped.keys()].filter((id) => !preferredOrder.includes(id)),
	].slice(0, MAX_BLOCKS);

	const workParts = blockIds
		.map((id) => formatBlockWork(id, grouped.get(id) || []))
		.filter(Boolean);

	const extras = scope === 'ideate' ? describeIdeateExtras(graphData) : [];
	const essence = shortenEssence(board.summary) || shortenEssence(board.vision);

	if (!workParts.length && !extras.length) {
		if (omitEmpty) return '';
		if (scope === 'ideate') {
			return preferTeam
				? 'Team Ideate has no positioning outputs yet.'
				: EMPTY_BOARD.ideate;
		}
		return nodes.length
			? `${scopeLabel} has work in progress, but no labelled ideas yet.`
			: EMPTY_BOARD[scope];
	}

	if (!workParts.length && extras.length) {
		const extrasText = extras.join(', ');
		if (essence) {
			return `${scopeLabel} is exploring ${essence}. The positioning map currently has ${extrasText}.`;
		}
		return `On ${scopeLabel}, the positioning map currently has ${extrasText}.`;
	}

	const opener = essence
		? `${scopeLabel} is exploring ${essence}.`
		: `So far on ${scopeLabel}`;
	const workSentence = essence
		? ` ${workParts.join('. ')}.`
		: `, ${workParts.join('. ')}.`;

	if (extras.length) {
		return `${opener}${workSentence} It also has ${extras.join(', ')}.`.replace(/\.\./g, '.');
	}

	return `${opener}${workSentence}`.replace(/\.\./g, '.');
};

/** Compact Idea Box progress. Prefer real Idea Box likes (team or personal) over graph/memory proxies. */
export const summarizeIdeaBoxProgress = ({
	board,
	memory,
	omitEmpty = false,
	ideaBoxLikes,
	teamIdeaBox = false,
}: {
	board?: Omit<Board, 'addUser'> | null;
	memory?: IAgentMemory | null;
	omitEmpty?: boolean;
	/** Actual Idea Box likes — for catch-up this should be the Team Idea Box list. */
	ideaBoxLikes?: LikedItem[] | null;
	teamIdeaBox?: boolean;
}): string => {
	if (ideaBoxLikes) {
		const cards = ideaBoxLikes.flatMap((item) => {
			try {
				return getPositioningOutputSnapshots(item);
			} catch {
				const title = (item.title || item.text || '').trim();
				return title ? [{ title }] : [];
			}
		});
		const boxLabel = teamIdeaBox ? 'Team Idea Box' : 'Idea Box';
		if (!cards.length) {
			return omitEmpty
				? ''
				: `The ${boxLabel} is still empty — heart useful nodes and results as you go.`;
		}
		const samples = cards
			.map((card) => ('title' in card ? String(card.title || '').trim() : ''))
			.filter(Boolean)
			.slice(0, 3);
		const countLine = `${cards.length} saved item${cards.length === 1 ? '' : 's'}`;
		const sampleLine = samples.length ? ` — including ${joinLabels(samples)}` : '';
		return `${boxLabel}: ${countLine}${sampleLine}.`;
	}

	const state = memory?.sessionState;
	const likedInspire = countLikedNodes(board?.inspire?.data);
	const personaLabels = (state?.savedPersonaLabels || []).filter(Boolean).slice(0, 3);
	const motivationLabels = (state?.savedMotivationLabels || []).filter(Boolean).slice(0, 3);
	const personaCount = new Set(state?.savedPersonaResultIds || []).size;
	const motivationCount = new Set(state?.savedMotivationResultIds || []).size;
	const researchCount = new Set(state?.researchSavedResultIds || []).size;
	const visualCount = new Set(state?.visualSavedResultIds || []).size;

	const parts: string[] = [];
	if (likedInspire > 0) {
		parts.push(
			`${likedInspire} hearted Inspire node${likedInspire === 1 ? '' : 's'}`
		);
	}
	if (personaCount > 0) {
		parts.push(
			personaLabels.length
				? `${personaCount} persona${personaCount === 1 ? '' : 's'} (${joinLabels(personaLabels)})`
				: `${personaCount} persona${personaCount === 1 ? '' : 's'}`
		);
	}
	if (motivationCount > 0) {
		parts.push(
			motivationLabels.length
				? `${motivationCount} motivation${motivationCount === 1 ? '' : 's'} (${joinLabels(motivationLabels)})`
				: `${motivationCount} motivation${motivationCount === 1 ? '' : 's'}`
		);
	}
	if (researchCount > 0) {
		parts.push(`${researchCount} saved research item${researchCount === 1 ? '' : 's'}`);
	}
	if (visualCount > 0) {
		parts.push(`${visualCount} liked visual${visualCount === 1 ? '' : 's'}`);
	}

	if (!parts.length) {
		return omitEmpty ? '' : 'The Idea Box is still empty — heart useful nodes and results as you go.';
	}
	return `Idea Box so far: ${parts.join('; ')}.`;
};

/**
 * Team-oriented catch-up summary: Inspire + Team Ideate + Team Synthesise + Idea Box.
 * Template-based from board graphs and agent memory (cheap, no LLM).
 */
export const summarizeAllBoards = (
	board: Omit<Board, 'addUser'> | null | undefined,
	memory?: IAgentMemory | null,
	options?: {
		omitEmpty?: boolean;
		ideaBoxLikes?: LikedItem[] | null;
		teamIdeaBox?: boolean;
	}
): string => {
	const omitEmpty = options?.omitEmpty === true;
	const boardParts = (['inspire', 'ideate', 'moodboard'] as FlowyPlanBoard[]).map((scope) =>
		summarizeBoardGraph({ board, scope, memory, preferTeam: true, omitEmpty })
	);
	const ideaBox = summarizeIdeaBoxProgress({
		board,
		memory,
		omitEmpty,
		ideaBoxLikes: options?.ideaBoxLikes,
		teamIdeaBox: options?.teamIdeaBox,
	});
	const juice = [...boardParts, ideaBox].filter((part) => part && !isEmptyBoardSummary(part));
	if (juice.length) return juice.join(' ');
	return omitEmpty ? 'The session is just getting started — there is not much on the boards yet.' : '';
};

export const PARTICIPANT_HOST_OWNS_PLAN_HINT =
	'The host owns the team plan — only they can edit or advance it.';

export const buildSharedCatchupMessage = ({
	sharedSession,
	boardTitle,
	boardVision,
	boardSummary,
	boardContents,
	participantLocations,
	alreadyJoinedActiveStep = false,
}: {
	sharedSession: {
		goal?: string | null;
		activeStepId?: string | null;
		plan?: { steps?: Array<{ id: string; label: string }> | null } | null;
	} | null;
	boardTitle: string;
	boardVision?: string | null;
	boardSummary?: string | null;
	boardContents: string;
	participantLocations: string;
	alreadyJoinedActiveStep?: boolean;
}): string => {
	const activeStep = sharedSession?.plan?.steps?.find(
		(step) => step.id === sharedSession.activeStepId
	);
	const normalizedBoardTitle = boardTitle.trim() || 'this board';
	const activeStepLabel = activeStep?.label.trim() || 'current step';
	const goal = sharedSession?.goal || boardSummary || boardVision || normalizedBoardTitle;
	const stepLine = activeStep
		? alreadyJoinedActiveStep
			? `You are on **${activeStepLabel}** with the team.`
			: `The team is currently on **${activeStepLabel}**.`
		: 'The team has not started a shared plan step yet.';
	const nextLine = activeStep
		? alreadyJoinedActiveStep
			? `A good next move is to scan what is already on the board for **${activeStepLabel}**, then add or heart one useful contribution.`
			: `A good next move is to join **${activeStepLabel}**, scan what is already on the board, and then add or heart one useful contribution.`
		: 'A good next move is to agree the board goal and accept a shared plan.';

	return [
		`Here is a catch-up for **${normalizedBoardTitle}**.`,
		`Goal: ${goal}`,
		stepLine,
		boardContents,
		participantLocations,
		PARTICIPANT_HOST_OWNS_PLAN_HINT,
		nextLine,
	]
		.filter(Boolean)
		.join('\n\n');
};

export const formatParticipantBoardLocations = (
	participants: Array<Pick<IFlowyParticipantState, 'role' | 'presenceStatus' | 'currentBoard' | 'displayName'>>
): string => {
	const online = participants.filter((participant) => participant.presenceStatus !== 'left');
	if (!online.length) {
		return 'You are the only person on the board right now.';
	}
	const grouped = new Map<string, string[]>();
	online.forEach((participant, index) => {
		const board = participant.currentBoard
			? BOARD_LABEL[participant.currentBoard]
			: 'an unspecified board';
		const names = grouped.get(board) || [];
		names.push(
			participant.displayName ||
				(participant.role === 'host' ? 'the host' : `collaborator ${index + 1}`)
		);
		grouped.set(board, names);
	});
	const lines = [...grouped.entries()].map(
		([board, names]) => `${names.join(', ')} ${names.length === 1 ? 'is' : 'are'} on ${board}`
	);
	return `Right now: ${lines.join('; ')}.`;
};

export const personalTeamSignpost = (
	board: FlowyPlanBoard,
	isTeam = false
): string => {
	if (board === 'inspire') return '';
	const boardName = BOARD_LABEL[board];
	if (board === 'ideate') {
		return 'You are starting in **Personal Ideate**, where Flowy and the analyses use only the Inspire ideas you added. **Team Ideate** uses everyone\'s Inspire ideas and shows the positioning outputs shared by the team, so its analyses and generated directions may differ.';
	}
	if (isTeam) {
		return `You are starting in **Team ${boardName}**. Flowy uses the outputs shared on this board and the Team Note. Switch to **Personal** when you want to develop something privately before sharing it.`;
	}
	return `You are starting in **Personal ${boardName}**, where Flowy uses only your private outputs and Personal Note. Share individual outputs when they are ready for the team, or switch to **Team** to work from the shared board.`;
};

const ideateGraphForUser = ({
	board,
	boardUser,
	isTeam,
}: {
	board: Omit<Board, 'addUser'>;
	boardUser: BoardUser;
	isTeam: boolean;
}): NCGraphData | undefined => {
	const userIndex = board.users.findIndex((user) => user.id === boardUser.id);
	if (userIndex < 0) return undefined;
	const fieldName = getProjectionName(userIndex, board.users.length).replace(
		'boardsUser',
		'user'
	) as keyof Pick<
		Omit<Board, 'addUser'>,
		'userOne' | 'userTwo' | 'userThree' | 'userFour' | 'userFive' | 'userSix' | 'userSeven' | 'userEight'
	>;
	const userData = board[fieldName];
	return (isTeam ? userData?.ideateTeam : userData?.ideate)?.data;
};

export const ideateContextSwitchSummary = ({
	isTeam,
	board,
	boardUser,
}: {
	isTeam: boolean;
	board?: Omit<Board, 'addUser'> | null;
	boardUser?: BoardUser | null;
}): string => {
	const mode = isTeam ? 'Team' : 'Personal';
	if (!board || !boardUser) {
		return `You are now in **${mode} Ideate**. ${
			isTeam
				? 'Flowy and the analyses now use everyone\'s Inspire ideas and the outputs shared with the team.'
				: 'Flowy and the analyses now use only the Inspire ideas you added and your personal outputs.'
		} This is a different analysis context, so the positioning directions may change.`;
	}

	const allInspireNodes = (board.inspire?.data?.nodes || []).filter((node) => !node.hidden);
	const personalInspireNodes = allInspireNodes.filter(
		(node) => node.colour === boardUser.colour
	);
	const contextNodes = isTeam ? allInspireNodes : personalInspireNodes;
	const labels = pickLabels(contextNodes, 3);
	const examples = labels.length ? ` — including ${joinLabels(labels)}` : '';
	const otherContextCount = isTeam ? personalInspireNodes.length : allInspireNodes.length;
	const graphData = ideateGraphForUser({ board, boardUser, isTeam });
	const extras = describeIdeateExtras(graphData);
	const mapState = extras.length ? ` The current map has ${extras.join(', ')}.` : '';
	const comparison = isTeam
		? `Your Personal context would use ${otherContextCount} idea${otherContextCount === 1 ? '' : 's'} that you added.`
		: `Team context would use all ${otherContextCount} visible Inspire idea${otherContextCount === 1 ? '' : 's'}.`;

	return `You are now in **${mode} Ideate**. Flowy and the analyses use ${
		isTeam ? 'all' : 'your'
	} ${contextNodes.length} visible Inspire idea${contextNodes.length === 1 ? '' : 's'}${examples}. ${comparison}${mapState} Because the evidence context is different, the analyses and generated positioning outputs may differ.`;
};

const synthesiseGraphForUser = ({
	board,
	boardUser,
	isTeam,
}: {
	board: Omit<Board, 'addUser'>;
	boardUser: BoardUser;
	isTeam: boolean;
}): NCGraphData | undefined => {
	if (isTeam) return board.moodboard?.data;
	const userIndex = board.users.findIndex((user) => user.id === boardUser.id);
	if (userIndex < 0) return undefined;
	const fieldName = getProjectionName(userIndex, board.users.length).replace(
		'boardsUser',
		'user'
	) as keyof Pick<
		Omit<Board, 'addUser'>,
		'userOne' | 'userTwo' | 'userThree' | 'userFour' | 'userFive' | 'userSix' | 'userSeven' | 'userEight'
	>;
	return board[fieldName]?.moodboard?.data;
};

export const synthesiseContextSwitchSummary = ({
	isTeam,
	board,
	boardUser,
	isReturn = false,
}: {
	isTeam: boolean;
	board?: Omit<Board, 'addUser'> | null;
	boardUser?: BoardUser | null;
	isReturn?: boolean;
}): string => {
	const mode = isTeam ? 'Team' : 'Personal';
	if (!board || !boardUser) {
		return `You are now in **${mode} Synthesise**. Synthesise is where you turn useful ideas and research into concrete outputs. Add material from the **Idea Box** or **Notepad**; liking a node on Inspire also adds it directly to Synthesise. ${
			isTeam
				? 'You can switch to **Personal Synthesise** first when you want to develop something privately.'
				: 'Create privately here, then share outputs with the team when they are ready.'
		}`;
	}

	const activeGraph = synthesiseGraphForUser({ board, boardUser, isTeam });
	const activeNodes = (activeGraph?.nodes || []).filter((node) => !node.hidden);
	const labels = pickLabels(activeNodes, 3);
	const examples = labels.length ? `, including ${joinLabels(labels)}` : '';
	const nodeSummary = `${activeNodes.length} node${activeNodes.length === 1 ? '' : 's'}${examples}`;
	if (isReturn) {
		if (activeNodes.length === 0) {
			return `**${mode} Synthesise** is ready. Add material from the **Idea Box** or **Notepad**${
				isTeam ? ', or bring over work from Personal Synthesise' : ''
			}, then create an output from the board nodes.`;
		}
		return `There ${activeNodes.length === 1 ? 'is' : 'are'} ${nodeSummary} on **${mode} Synthesise**. Add more from the **Idea Box**, **Notepad**${
			isTeam ? ', or Personal Synthesise' : ''
		}, or start creating outputs based on the board nodes.`;
	}
	const boardState = activeNodes.length
		? ` There ${activeNodes.length === 1 ? 'is' : 'are'} currently ${nodeSummary}.`
		: '';
	return `You are now in **${mode} Synthesise**. Synthesise is where you turn the material you have gathered into concrete outputs. Add nodes from the **Idea Box** or **Notepad**; liking nodes on Inspire also adds them directly here.${boardState} ${
		isTeam
			? 'You can also begin in **Personal Synthesise** and share the work when it is ready.'
			: 'Develop the work privately here, then share outputs with the team when they are ready.'
	}`;
};

export const pickSynthesiseSuggestedNode = ({
	board,
	boardUser,
	isTeam,
}: {
	board?: Omit<Board, 'addUser'> | null;
	boardUser?: BoardUser | null;
	isTeam: boolean;
}): { uid: string; label: string } | null => {
	if (!board || !boardUser) return null;
	const graph = synthesiseGraphForUser({ board, boardUser, isTeam });
	const candidates = (graph?.nodes || []).filter(
		(node) => !node.hidden && Boolean(node.uid) && isNodeLabel(node.label)
	);
	const linkedCounts = new Map<string, number>();
	for (const link of graph?.links || []) {
		linkedCounts.set(link.source.uid, (linkedCounts.get(link.source.uid) || 0) + 1);
		linkedCounts.set(link.target.uid, (linkedCounts.get(link.target.uid) || 0) + 1);
	}
	const candidate = candidates.sort((left, right) => {
		const score = (node: NCNode): number =>
			Object.values(node.likes || {}).filter(Boolean).length * 3 +
			(node.positioningOutput ? 4 : 0) +
			(node.src ? 1 : 0) +
			Math.min(3, linkedCounts.get(node.uid) || 0) -
			(node.synthesiseRecipe ? 4 : 0);
		return score(right) - score(left) || (right.created || 0) - (left.created || 0);
	})[0];
	return candidate ? { uid: candidate.uid, label: compactNodeLabel(candidate) } : null;
};

export const shareToTeamPrompt = (label?: string | null): string => {
	const subject = label?.trim() ? `"${label.trim()}"` : 'this result';
	return `Would you like to share ${subject} with the team? **Share to Team** will move you onto the Team board.`;
};

export const collaboratorBoardSwitchSummary = ({
	displayName,
	board,
}: {
	displayName: string;
	board: FlowyPlanBoard;
	boardModel?: Omit<Board, 'addUser'> | null;
	memory?: IAgentMemory | null;
}): string => `${displayName} is now on ${BOARD_LABEL[board]}.`;

export const HOST_PLAN_OWNER_JOIN_HINT =
	"You're the owner of the team's plan. Whenever you progress to the next step, the rest of the team gets a message inviting them to join you there.";

export const hostParticipantJoinedSummary = (displayName: string): string =>
	`${displayName} joined the session. ${HOST_PLAN_OWNER_JOIN_HINT}`;
